import React, { useEffect, useMemo, useState } from "react";
import {
  Carousel,
  CarouselControl,
  CarouselIndicators,
  CarouselItem,
} from "reactstrap";
import PhotoSphereViewer from "../PhotoSphereViewer/PhotoSphereViewer";
import "./GalleryCarousel.css";
import api from "../../api/api";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import ImageViewer from "../ImageViewer/ImageViewer";
import useProjectTypeStore from "../../store/useProjectTypeStore";
import ModalConfirmation from "../ModalConfirmation/ModalConfirmation";
import ModalMessage from "../ModalMessage/ModalMessage";
import useDetectionDetailsStore from "../../store/useDetectionDetailsStore";

const classNames = [
  "cross_head",
  "lamp",
  "lamp_arm",
  "transformer",
  "telecom_box",
  "wire_reserve",
  "drop_out_fuse",
  "fiber_optic_junction_box",
  "insulator",
  "pole",
  "tree",
];

const classNameMap = {
  cross_head: "Cruzeta",
  lamp: "Lâmpada",
  lamp_arm: "Braço de luminária",
  transformer: "Transformador",
  telecom_box: "Caixa de telecom",
  wire_reserve: "Reserva de fios",
  drop_out_fuse: "Chave fusível",
  fiber_optic_junction_box: "Caixa junção fibra óptica",
  insulator: "Isolador",
  pole: "Poste",
  tree: "Árvore",
};

const GalleryCarousel = ({ imageArray, index, detectedObjectType }) => {
  const [activeIndex, setActiveIndex] = useState(index);
  const [animating, setAnimating] = useState(false);
  const [allDetections, setAllDetections] = useState(false);
  const [allDetectionsArray, setAllDetectionsArray] = useState([]);
  const [checkboxFilter, setCheckboxFilter] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [modalConfirmation, setModalConfirmation] = useState(false);

  const projectType = useProjectTypeStore((state) => state.projectType);
  const [fetchDetectionsEndPoint, setFetchDetectionsEndPoint] = useState("");
  const [messageModal, setMessageModal] = useState(null);
  const [message, setMessage] = useState("");

  const { setListDetections, modalContent } = useDetectionDetailsStore();

  const toggleMessageModal = () => setMessageModal(!messageModal);

  const toggleModalConfirmation = () =>
    setModalConfirmation(!modalConfirmation);

  useEffect(() => {
    const endpoint = projectType === "GROUND" ? `detections-obb` : `detections`;

    if (endpoint) {
      setFetchDetectionsEndPoint(endpoint);
    }
  }, [projectType]);

  useEffect(() => {
    if (fetchDetectionsEndPoint) {
      fetchDetections(activeIndex, fetchDetectionsEndPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, detectedObjectType, fetchDetectionsEndPoint]);

  const fetchDetections = async (index, endpoint) => {
    setLoading(true);

    try {
      const { data } = await api.get(
        `/images/${imageArray[activeIndex].image.id}/${endpoint}`,
      );

      const detectionData =
        endpoint === "detections-obb"
          ? processGroundDetections(index, data)
          : processAerialDetections(index, data);

      setAllDetectionsArray(detectionData);
    } catch (error) {
      console.error(error.message);
    }
    setLoading(false);
  };

  function processGroundDetections(index, data) {
    const detectedObjectId = imageArray[index].detectedObjectId;

    return data.filter((item) => {
      if (item.objectType === "pole") {
        return item.detectedObjectId === detectedObjectId;
      }
      return detectedObjectType === "pole"
        ? item.poleId === detectedObjectId
        : item.objectType !== "tree";
    });
  }

  function processAerialDetections(index, data) {
    const detectedObjectId = imageArray[index].public_lighting_point_id;

    return data.filter((item) => {
      return item.public_lighting_point_id === detectedObjectId;
    });
  }

  const navigate = async (nextIndex) => {
    if (animating) return;
    setActiveIndex(nextIndex);

    if (allDetections) {
      await fetchDetections(nextIndex, fetchDetectionsEndPoint);
    }
  };

  const next = () =>
    navigate(activeIndex === imageArray.length - 1 ? 0 : activeIndex + 1);
  const previous = () =>
    navigate(activeIndex === 0 ? imageArray.length - 1 : activeIndex - 1);
  const goToIndex = (newIndex) => navigate(newIndex);

  const typeCounts = useMemo(
    () =>
      allDetectionsArray.reduce((counts, item) => {
        counts[item.objectType] = (counts[item.objectType] || 0) + 1;
        return counts;
      }, {}),
    [allDetectionsArray]
  );

  const updateFilters = (checked, categoryFilter) => {
    setCheckboxFilter((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(categoryFilter);
      } else {
        next.delete(categoryFilter);
      }
      return next;
    });
  };

  const filteredProducts = () =>
    checkboxFilter.size === 0
      ? allDetectionsArray
      : allDetectionsArray.filter((p) => checkboxFilter.has(p.objectType));

  const handleAllDetections = async () => {
    await fetchDetections(activeIndex, fetchDetectionsEndPoint); // Fetch detections for the activeIndex
    setAllDetections(!allDetections);
  };

  const handleDelete = async () => {
    try {
      const objectId = imageArray[activeIndex].id;
      const existingDetection = imageArray.find((d) => d.id === objectId);

      if (existingDetection) {
        await api.delete(`/detections-obb/${objectId}`);

        const updatedList = imageArray.filter((d) => d.id !== objectId);

        setListDetections(updatedList);
        modalContent.onDelete(objectId);

        toggleModalConfirmation();
      }
    } catch (error) {
      setMessage(`Erro ao deletar detecção #${imageArray[activeIndex].id}!`);
    } finally {
      setTimeout(() => {
        toggleModalConfirmation();
      }, 2000);
    }
  };

  return (
    <>
      {modalConfirmation && (
        <ModalConfirmation
          toggle={toggleModalConfirmation}
          modal={modalConfirmation}
          text={`Deseja remover a detecção #${imageArray[activeIndex].id}?`}
          confirmFunction={handleDelete}
        />
      )}

      {messageModal && (
        <ModalMessage
          isOpen={true}
          message={message}
          toggle={toggleMessageModal}
        />
      )}

      {!allDetections && (
        <div className="remove-btn-container">
          <small
            className="btn btn-danger remove-btn"
            onClick={toggleModalConfirmation}
          >
            Remover detecção
          </small>
        </div>
      )}

      {detectedObjectType === "pole" && (
        <div className="checkbox-container">
          <div className="all-detections-checkbox">
            <input
              type="checkbox"
              id="type-checkbox"
              checked={allDetections}
              onChange={handleAllDetections}
            />
            <label htmlFor="type-checkbox">
              Visualizar todas as detecções.
            </label>
          </div>

          {allDetections && (
            <div className="filter-options-container-carousel">
              {classNames.map((className) => {
                const count = typeCounts[className] || 0;
                return (
                  count > 0 && (
                    <div key={className} className="filter-option-carousel">
                      <input
                        type="checkbox"
                        id={className}
                        onChange={(e) =>
                          updateFilters(e.target.checked, className)
                        }
                      />
                      <label htmlFor={className}>
                        <strong>{`${classNameMap[className]} (${count})`}</strong>
                      </label>
                    </div>
                  )
                );
              })}
            </div>
          )}
        </div>
      )}

      <Carousel
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        interval={false}
      >
        <CarouselIndicators
          items={imageArray}
          activeIndex={activeIndex}
          onClickHandler={goToIndex}
        />
        {imageArray.map((item, idx) => (
          <CarouselItem
            key={Math.random()}
            onExiting={() => setAnimating(true)}
            onExited={() => setAnimating(false)}
            active={idx === activeIndex}
          >
            {!loading && idx === activeIndex ? (
              projectType === "GROUND" ? (
                <PhotoSphereViewer
                  containerId={`viewer-${idx}`}
                  imgSrc={item.image}
                  detection={allDetections ? filteredProducts() : item}
                  height={allDetections ? "75vh" : "80vh"}
                />
              ) : (
                <ImageViewer imageUrl={item.image} detection={item} />
              )
            ) : (
              loading && (
                <div style={{ height: allDetections ? "75vh" : "80vh" }}>
                  <LoadingIcon />
                </div>
              )
            )}
          </CarouselItem>
        ))}

        {imageArray.length > 1 && (
          <>
            <CarouselControl
              direction="prev"
              directionText="Previous"
              onClickHandler={previous}
            />
            <CarouselControl
              direction="next"
              directionText="Next"
              onClickHandler={next}
            />
          </>
        )}
      </Carousel>
    </>
  );
};

export default GalleryCarousel;
